import { useCallback, useEffect, useRef, useState } from "react";
import { LuPrinter, LuTriangleAlert, LuCheck } from "react-icons/lu";
import { useSelector } from "react-redux";
import { TpvDialog } from "../../components/TpvDialog";
import { getPrinterStatus } from "../../api/printers";
import "./printerPanel.css";
const labels = {
  connected: "Conectada",
  disconnected: "Sin conexión",
  unconfigured: "Sin configurar",
  unavailable: "No disponible",
  invalid: "Configuración no válida",
};
export function PrinterPanel({ disabled = false }) {
  const pointId = useSelector(
    (s) =>
      s.aplicationConfig.corporationData?.pointConfig
        ?.PointOfSaleConfigurationId,
  );
  const [open, setOpen] = useState(false),
    [data, setData] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const generation = useRef(0),
    flight = useRef(false);
  const load = useCallback(async () => {
    if (flight.current) return;
    flight.current = true;
    const current = ++generation.current;
    setBusy(true);
    setError("");
    setData(null);
    try {
      const r = await getPrinterStatus();
      if (current !== generation.current) return;
      if (r?.error !== false || !Array.isArray(r.data?.printers)) throw Error();
      if (r.data.pointOfSaleConfigurationId !== pointId) throw Error();
      setData(r.data);
    } catch {
      if (current === generation.current)
        setError(
          "No se pudo consultar el estado de las impresoras. Vuelve a intentarlo.",
        );
    } finally {
      if (current === generation.current) {
        flight.current = false;
        setBusy(false);
      }
    }
  }, [pointId]);
  const invalidate = useCallback(() => {
    generation.current++;
    flight.current = false;
  }, []);
  useEffect(() => {
    flight.current = false;
    if (pointId) load();
    else {
      generation.current++;
      setData(null);
      setError("Selecciona un puesto para consultar sus impresoras.");
    }
    return invalidate;
  }, [pointId, load, invalidate]);
  const currentData =
    data?.pointOfSaleConfigurationId === pointId ? data : null;
  const warning = currentData?.printers.some((p) => p.status !== "connected");
  const adminUrl = window.location.hostname.includes(".tpv.")
    ? `${window.location.protocol}//${window.location.host.replace(".tpv.", ".admin.")}/configuration#107`
    : null;
  return (
    <>
      <button
        type="button"
        className={`tpvPrinterControl ${warning || error ? "has-warning" : ""}`}
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label="Estado y configuración de impresoras"
      >
        {warning || error ? (
          <LuTriangleAlert aria-hidden="true" />
        ) : (
          <LuPrinter aria-hidden="true" />
        )}
        <span>Impresoras</span>
      </button>
      {open && (
        <TpvDialog
          busy={busy}
          title="Estado del hardware"
          onClose={() => setOpen(false)}
          className="tpvPrinterDialog"
        >
          <div className="tpvDialogBody">
            {busy ? (
              <p role="status">Comprobando impresoras…</p>
            ) : error ? (
              <p role="alert" className="tpvPrinterError">
                {error}
              </p>
            ) : (
              currentData?.printers.map((p, i) => (
                <div
                  className="tpvPrinterRow"
                  key={`${p.kind}-${p.device}-${i}`}
                >
                  <div>
                    <strong>
                      {p.label}
                      {p.device ? ` · ${p.device}` : ""}
                    </strong>
                    {p.name && (
                      <p>
                        {p.name} ·{" "}
                        <span>
                          {p.ip}:{p.port}
                        </span>
                      </p>
                    )}
                  </div>
                  <span className={`tpvPrinterBadge is-${p.status}`}>
                    {p.status === "connected" ? (
                      <LuCheck aria-hidden="true" />
                    ) : (
                      <LuTriangleAlert aria-hidden="true" />
                    )}
                    {labels[p.status] || "No disponible"}
                  </span>
                </div>
              ))
            )}
            <p className="tpvPrinterHelp">
              La conexión de red no confirma que haya papel ni que el ticket se
              haya impreso.
            </p>
            {currentData?.checkedAt && (
              <p className="tpvPrinterHelp">
                Última comprobación:{" "}
                {new Date(currentData.checkedAt).toLocaleTimeString("es-ES", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </p>
            )}
          </div>
          <footer>
            {adminUrl && (
              <a href={adminUrl} target="_blank" rel="noopener noreferrer">
                Configurar en Admin
              </a>
            )}
            <button type="button" onClick={load} disabled={busy}>
              {busy ? "Comprobando…" : "Reintentar"}
            </button>
          </footer>
        </TpvDialog>
      )}
    </>
  );
}
